import { FC, MouseEvent as ReactMouseEvent, ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import {
  HORMONE_TRENDS_LEFT_GUTTER_WIDTH,
  HORMONE_TRENDS_RIGHT_GUTTER_WIDTH,
  HORMONE_TRENDS_SERIES,
  HORMONE_TRENDS_SERIES_STYLES,
} from '../../../common/utils/services/hormone-trends-utils';
import {
  DayColumn,
  HormoneTrendsSeriesId,
} from '../../../common/model/type/hormone-trends.type';
import Colours from '../../../design-system/colours';
import Variables from '../../../design-system/variables';

export type HormoneTrendsTooltipValues = Record<
  HormoneTrendsSeriesId,
  number | null
>;

export interface OvHormoneTrendsInteractionLayerProps {
  children: ReactNode;
  columns: DayColumn[];
  selectedSeries: HormoneTrendsSeriesId[];
  selectedIndex?: number;
  tooltipValues: HormoneTrendsTooltipValues[];
  onSelectColumn: (column: DayColumn) => void;
  leftGutterWidth?: number;
  rightGutterWidth?: number;
}

const OvHormoneTrendsInteractionLayer: FC<
  OvHormoneTrendsInteractionLayerProps
> = ({
  children,
  columns,
  selectedSeries,
  selectedIndex,
  tooltipValues,
  onSelectColumn,
  leftGutterWidth = HORMONE_TRENDS_LEFT_GUTTER_WIDTH,
  rightGutterWidth = HORMONE_TRENDS_RIGHT_GUTTER_WIDTH,
}) => {
  const { t } = useTranslation();
  const [hoveredIndex, setHoveredIndex] = useState<number>();

  const indexFromPointer = (
    event: ReactMouseEvent<HTMLDivElement>
  ): number | undefined => {
    const bounds = event.currentTarget.getBoundingClientRect();
    const trackWidth = bounds.width - leftGutterWidth - rightGutterWidth;
    const trackX = event.clientX - bounds.left - leftGutterWidth;

    if (trackWidth <= 0 || trackX < 0 || trackX >= trackWidth) {
      return undefined;
    }

    return Math.min(
      columns.length - 1,
      Math.floor(trackX / (trackWidth / columns.length))
    );
  };

  const handlePointerMove = (event: ReactMouseEvent<HTMLDivElement>) =>
    setHoveredIndex(indexFromPointer(event));

  const handlePointerClick = (event: ReactMouseEvent<HTMLDivElement>) => {
    const index = indexFromPointer(event);

    if (index !== undefined) {
      onSelectColumn(columns[index]);
    }
  };

  const hoveredColumn =
    hoveredIndex === undefined ? undefined : columns[hoveredIndex];
  const hoveredValues =
    hoveredIndex === undefined ? undefined : tooltipValues[hoveredIndex];

  const dayHeading = (column: DayColumn): string =>
    column.cycleDay === undefined
      ? column.dateLabel
      : `${t('dashboard.chart.day')} ${column.cycleDay} - ${column.dateLabel}`;

  return (
    <InteractionRegion
      data-testid="hormone-trends-interaction-region"
      onMouseMove={handlePointerMove}
      onMouseLeave={() => setHoveredIndex(undefined)}
      onClick={handlePointerClick}
    >
      {children}
      <ColumnLayer
        $columnCount={columns.length}
        $leftGutterWidth={leftGutterWidth}
        $rightGutterWidth={rightGutterWidth}
        data-testid="hormone-trends-column-hit-layer"
        data-column-count={columns.length}
      >
        {columns.map((column) => {
          const isHovered = column.index === hoveredIndex;
          const isSelected = column.index === selectedIndex;

          return (
            <ColumnTarget
              key={column.key}
              type="button"
              aria-label={dayHeading(column)}
              aria-pressed={isSelected}
              data-column-key={column.key}
              data-hovered={isHovered || undefined}
              $isHovered={isHovered}
              $isSelected={isSelected}
              onFocus={() => setHoveredIndex(column.index)}
              onBlur={() => setHoveredIndex(undefined)}
              onClick={(event) => {
                event.stopPropagation();
                onSelectColumn(column);
              }}
            />
          );
        })}
      </ColumnLayer>

      {hoveredColumn && hoveredValues && (
        <CombinedTooltip
          role="tooltip"
          data-testid="hormone-trends-combined-tooltip"
          style={{
            left: `calc(${leftGutterWidth}px + (100% - ${
              leftGutterWidth + rightGutterWidth
            }px) * ${(hoveredColumn.index + 0.5) / columns.length})`,
          }}
        >
          <TooltipHeading>{dayHeading(hoveredColumn)}</TooltipHeading>
          <TooltipValues>
            {selectedSeries.map((seriesId) => {
              const series = HORMONE_TRENDS_SERIES[seriesId];
              const value = hoveredValues[seriesId];

              return (
                <TooltipValue key={seriesId}>
                  <SeriesName
                    $colour={HORMONE_TRENDS_SERIES_STYLES[seriesId].colour}
                  >
                    {series.displayName}
                  </SeriesName>
                  <span>
                    {value === null ? '—' : value.toFixed(2)} {series.unit}
                  </span>
                </TooltipValue>
              );
            })}
          </TooltipValues>
        </CombinedTooltip>
      )}
    </InteractionRegion>
  );
};

export default OvHormoneTrendsInteractionLayer;

const InteractionRegion = styled.div`
  position: relative;
`;

const ColumnLayer = styled.div<{
  $columnCount: number;
  $leftGutterWidth: number;
  $rightGutterWidth: number;
}>`
  position: absolute;
  z-index: 2;
  top: 0;
  right: ${({ $rightGutterWidth }) => $rightGutterWidth}px;
  bottom: 0;
  left: ${({ $leftGutterWidth }) => $leftGutterWidth}px;
  display: grid;
  grid-template-columns: repeat(${({ $columnCount }) => $columnCount}, 1fr);
  pointer-events: none;
`;

const ColumnTarget = styled.button<{
  $isHovered: boolean;
  $isSelected: boolean;
}>`
  min-width: 0;
  padding: 0;
  border: 0;
  border-right: ${({ $isHovered, $isSelected }) =>
    $isHovered || $isSelected ? `1px solid ${Colours.OV_ACCENT_TEAL}` : 0};
  border-left: ${({ $isHovered, $isSelected }) =>
    $isHovered || $isSelected ? `1px solid ${Colours.OV_ACCENT_TEAL}` : 0};

  background-color: ${({ $isHovered, $isSelected }) =>
    $isSelected
      ? 'rgba(44, 122, 140, 0.24)'
      : $isHovered
      ? 'rgba(44, 122, 140, 0.14)'
      : 'transparent'};
  pointer-events: none;

  &:focus-visible {
    outline: 2px solid ${Colours.OV_ACCENT_BLUE};
    outline-offset: -2px;
  }
`;

const CombinedTooltip = styled.div`
  position: absolute;
  z-index: 3;
  top: 0.5rem;
  min-width: 10rem;
  overflow: hidden;
  border-radius: ${Variables.borderRadius.SMALL};
  box-shadow: ${Variables.boxShadow.defaultBox};
  transform: translateX(-50%);
  pointer-events: none;
`;

const TooltipHeading = styled.div`
  padding: 0.5rem 0.75rem;
  background-color: ${Colours.CD_TOOLTIP_HEADER};
  color: ${Colours.OV_WHITE};
  font-size: ${Variables.fontSizes.SMALL};
  font-weight: bold;
`;

const TooltipValues = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 0.5rem 0.75rem;
  background-color: ${Colours.OV_BASE};
  color: ${Colours.OV_WHITE};
  font-size: ${Variables.fontSizes.SMALL};
`;

const TooltipValue = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  white-space: nowrap;
`;

const SeriesName = styled.span<{ $colour: string }>`
  color: ${({ $colour }) => $colour};
  font-weight: bold;
`;
